import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
} from "firebase/firestore";
import { db } from "./setupFirebase";
import { SearchResult } from "./searchUtils";
import { Result } from "../generator/tagGenerator";
import { MembershipInfoResponse } from "../model/patreon";
import { IS_DEVELOPMENT, IS_TEST_CLOUD_FN } from "../test/SetupTest";
import { INPAINT_MODE } from "../zustand/historyStore";
import { Tag } from "../common/tagDefinitions";
import { useTagStore } from "../zustand/tagStore";
import { TagArtist } from "../common/userDefinitions";
import { useModelStore } from "../zustand/modelStore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const API2_URL = "https://autoapi.cdn2.io/Pornpen/";

export interface SendPromptInput {
  prompt: Result;
  generator: string;
  colorHint?: string;
  isPrivate: boolean;
  isTest?: boolean;
  directorImg?: string;
  model?: string;
  animated?: boolean;
}

interface SendPromptOutput {
  id?: string;
  error?: string;
  existing_task_id?: string;
  numTasks?: number;
}

export async function sendPrompt(data: SendPromptInput) {
  if (IS_TEST_CLOUD_FN) {
    data.isTest = true;
  }

  // TODO: Move to backend
  const generator = data.generator;
  const modelStore = useModelStore.getState();
  // Find the model for the generator
  const model = modelStore.models.find((m) => m.id === generator);
  if (model) {
    if (model.sdModel === "sdxl") {
      data.model = "sdxl";
    }
  }

  // Determine endpoint: _api2 generators use V2, all others use V1
  const endpoint = data.generator.includes("_api2")
    ? "https://cluster1api.cdn2.io/Pornpen/V2/SubmitPromptAuth"
    : API2_URL + "V1/SubmitPromptAuth";

  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    const idToken = await user.getIdToken();
    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: data
    });

    const response: any = await fetch(endpoint, {
      method: "POST",
      headers: headers,
      body: body,
    });

    const res = await response.json();
    return res.result as SendPromptOutput;
  } else {
    return {
      error: "User not authenticated"
    } as SendPromptOutput;
  }
}

export function getUserSavedImagesDoc(imageId: string, userId: string) {
  return doc(db, "users", userId, "savedImages", imageId);
}

export async function saveImage(
  imageId: string,
  userId: string,
  isPrivate: boolean
) {
  if (!userId || !imageId) {
    return;
  }

  // Add saved image to a firestore collection
  await setDoc(getUserSavedImagesDoc(imageId, userId), {
    createDate: new Date(),
    isPrivate: isPrivate,
  });
}

export async function deleteSavedImage(imageId: string, userId: string) {
  if (!userId || !imageId) {
    return;
  }

  // Delete saved image from a firestore collection
  await deleteDoc(getUserSavedImagesDoc(imageId, userId));
}

export async function getImageSaveStatus(
  imageId: string,
  userId: string
): Promise<boolean> {
  if (!userId || !imageId) {
    return false;
  }

  // Add saved image to a firestore collection
  const docSnap = await getDoc(getUserSavedImagesDoc(imageId, userId));
  return docSnap.exists();
}

// Function to get all saved images for a user
// These are stored in a subcollection /users/uid/savedImages/imageId
// emptyButMore is true if the result had items, but they were deleted or missing.
// This might false trigger an empty case, but there may be more.
export async function getSavedImages(
  userId: string,
  lastDoc?: any,
  startDate?: Date | null
): Promise<[imageResults: SearchResult[], lastDoc: any, reachedEnd?: boolean]> {
  if (!userId) {
    return [[], null];
  }

  const docLimit = 30;

  let q = query(
    collection(db, "users", userId, "savedImages"),
    orderBy("createDate", "desc"),
    limit(docLimit)
  );

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  } else if (startDate) {
    q = query(q, startAfter(startDate));
  }

  const querySnapshot = await getDocs(q);

  const savedImages = querySnapshot.docs.map((doc) => {
    return {
      id: doc.id,
      isPrivate: doc.data().isPrivate,
    };
  });

  // Loop through document ids and get the image urls
  // TODO do this server side to avoid many requests
  const imageUrls = await Promise.all(
    savedImages.map(async (savedImage) => {
      const imageId = savedImage.id;
      const docRef = savedImage.isPrivate
        ? doc(db, "users", userId, "private", imageId)
        : doc(db, "results", imageId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return {
          imageUrl: null,
          imageId: imageId,
          isPrivate: savedImage.isPrivate,
        };
      }

      const data = docSnap.data();
      return {
        imageUrl: data?.image_url.replace(".ai", ".art"),
        imageId: imageId,
        isPrivate: savedImage.isPrivate,
        isUpscaled:
          data?.edit_type === "upscale2" || data?.edit_type === "upscale",
      };
    })
  );

  const imageResults = imageUrls.filter((x) => x.imageUrl) as SearchResult[];

  const reachedEnd = querySnapshot.docs.length < docLimit;

  return [
    imageResults,
    querySnapshot.docs[querySnapshot.docs.length - 1],
    reachedEnd,
  ];
}

export async function getPrivateImages(
  userId: string,
  lastDoc?: any,
  startDate?: Date | null
): Promise<[imageResults: SearchResult[], lastDoc: any, reachedEnd: boolean]> {
  if (!userId) {
    return [[], null, true];
  }

  let q = query(
    collection(db, "users", userId, "private"),
    orderBy("createDate", "desc"),
    limit(30)
  );

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  } else if (startDate) {
    q = query(q, startAfter(startDate));
  }

  const querySnapshot = await getDocs(q);

  const imageResults = querySnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      imageUrl: data.image_url.replace(".ai", ".art"),
      imageId: doc.id,
      isUpscaled: data.edit_type === "upscale2" || data.edit_type === "upscale",
    };
  });

  const reachedEnd = querySnapshot.docs.length < 30;

  return [
    imageResults,
    querySnapshot.docs[querySnapshot.docs.length - 1],
    reachedEnd,
  ];
}

export async function submitPatreonOAuthCode(
  code: string,
  redirectUri: string
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  const idToken = await user.getIdToken();
  const response = await fetch(API2_URL + "SubmitPatreonOAuth", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  });

  if (!response.ok) throw new Error("Failed to submit Patreon OAuth");
  return await response.json();
}

export async function getSubscriberInfo(): Promise<MembershipInfoResponse> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: null
    });

    const response = await fetch(
      API2_URL + "GetSubscriberInfo",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return {} as MembershipInfoResponse;
    }

    const responseData = await response.json();
    // TODO Validate that the shape is correct
    return responseData.result as MembershipInfoResponse;
  } else {
    return {} as MembershipInfoResponse;
  }
}

export async function submitUpscale(
  imageId: string,
  isPrivate: boolean,
  upscalerId: string,
  size: number
): Promise<{ id: string }> {
  const auth = getAuth();

  return new Promise<{ id: string }>((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (!user) {
        reject(new Error("User not authenticated"));
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        };

        const body = JSON.stringify({
          imageId,
          isPrivate,
          upscalerId,
          size,
        });

        const response = await fetch(API2_URL + "SubmitUpscale", {
          method: "POST",
          headers: headers,
          body: body,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData?.error?.message || "Failed to submit upscale"
          );
        }

        const data = await response.json();
        resolve(data.result as { id: string });
      } catch (error) {
        reject(error);
      }
    });
  });
}

interface SubmitPaintingResult {
  id: string;
}

export async function submitPainting(
  imageId: string,
  promptData: Result,
  maskData: string,
  generator: string,
  maskMode: INPAINT_MODE,
  colorHint: string | null,
  isPrivate: boolean,
  enhancePaint: boolean,
  extraData?: any
) {
  let input: any = {
    imageId,
    promptData,
    generator,
    maskData,
    shouldInvert: maskMode === "keep",
    isMorph: maskMode === "morph",
    isPrivate,
    enhancePaint,
    isTest: IS_TEST_CLOUD_FN,
    ...extraData,
  };

  if (colorHint) {
    input.colorHint = colorHint;
  }

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const idToken = await user.getIdToken();
  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };

  const body = JSON.stringify(input);

  const response = await fetch(API2_URL + "SubmitPainting", {
    method: "POST",
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error?.message || "Failed to submit painting"
    );
  }

  const data = await response.json();
  return data.result as SubmitPaintingResult;
}

interface SubmitUncropResult {
  id: string;
}

export async function submitUncrop(
  imageId: string,
  scale: number,
  promptData: any,
  generator: string,
  offsetX: number,
  offsetY: number,
  isPrivate: boolean,
  model?: string
) {
  // SDXL uncrops are just inpaints
  if (model === "sdxl") {
    return await submitPainting(
      imageId,
      promptData,
      "",
      generator,
      "keep",
      null,
      isPrivate,
      false,
      {
        uncrop: true,
        scale: scale,
        offsetX: offsetX,
        offsetY: offsetY,
      }
    );
  }

  const auth = getAuth();

  return new Promise<SubmitUncropResult>((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (!user) {
        reject(new Error("User not authenticated"));
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        };

        const body = JSON.stringify({
          imageId,
          scale,
          promptData,
          generator,
          offsetX,
          offsetY,
          isPrivate,
        });

        const response = await fetch(API2_URL + "SubmitUncrop", {
          method: "POST",
          headers: headers,
          body: body,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData?.error?.message || "Failed to submit uncrop"
          );
        }

        const data = await response.json();
        resolve(data.result as SubmitUncropResult);
      } catch (error) {
        reject(error);
      }
    });
  });
}

export async function submitRecaptcha(token: string) {
  const response = await fetch(API2_URL + "SubmitRecaptcha", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error("Recaptcha verification failed");
}

interface SearchSqlResult {
  imageId: string;
  imageUrl: string;
  createTime: number;
}


export async function submitSearch(
  tags: string[],
  generator: string,
  source: "make" | "search",
  startBeforeTimestamp?: number,
  hideEdits?: boolean,
  disableSort?: boolean
): Promise<SearchSqlResult[]> {
  let input: any = { tags, generator, source };

  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: input
    });

    const response: any = await fetch(
      API2_URL + "search",
      // "https://abby-api.cdn2.io/Pornpen/search",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    return responseData.result as SearchSqlResult[];
  }
  else {
    return [];
  }
}

export async function searchExamplesFromATag(
  tags: string[],
  generator: string,
  source: "make" | "search",
  startBeforeTimestamp?: number,
  hideEdits?: boolean,
  disableSort?: boolean
): Promise<SearchSqlResult[]> {
  let input: any = { tags, generator, source };

  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: input
    });

    const response: any = await fetch(
      API2_URL + "searchExamplesFromATag",
      // "https://abby-api.cdn2.io/Pornpen/search",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    return responseData.result as SearchSqlResult[];
  }
  else {
    return [];
  }
}

export async function submitEnhance(
  imageId: string,
  generator: string,
  promptData: any,
  isPrivate: boolean,
  isUpscale: boolean
) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const idToken = await user.getIdToken();
  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };

  const body = JSON.stringify({
    enhanceResultId: imageId,
    generator,
    promptData,
    isPrivate,
    isTest: IS_TEST_CLOUD_FN,
    isUpscale,
  });

  const response = await fetch(API2_URL + "SubmitEnhance", {
    method: "POST",
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error?.message || "Failed to submit enhance"
    );
  }

  const data = await response.json();
  return data.result as any;
}

export async function submitFlag(imageId: string) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const auth = getAuth();
  if (auth.currentUser) {
    const idToken = await auth.currentUser.getIdToken();
    headers["Authorization"] = `Bearer ${idToken}`;
  }
  const response = await fetch(API2_URL + "SubmitFlag", {
    method: "POST",
    headers,
    body: JSON.stringify({ imageId }),
  });
  if (!response.ok) throw new Error("Failed to submit flag");
}

export async function deletePrivateImage(imageId: string) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { imageId }
    });

    const response = await fetch(
      API2_URL + "deletePrivateImage",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete private image");
    }
  } else {
    throw new Error("User not authenticated");
  }
}

export async function rateImage(
  imageId: string,
  rating: string,
  isVoting: boolean,
  isPrivate: boolean
) {
  const auth = getAuth();

  return new Promise<void>((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (user) {
        try {
          const idToken = await user.getIdToken();

          const headers = {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          };

          const body = JSON.stringify({
            data: { imageId, rating, isVoting, isPrivate }
          });

          const response = await fetch(API2_URL + "rateImage", {
            method: "POST",
            headers: headers,
            body: body,
          });

          if (!response.ok) {
            const errorData = await response.json();
            reject(new Error(errorData.error?.message || "Failed to rate image"));
            return;
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error("User not authenticated"));
      }
    });
  });
}

interface ImageRating {
  rating: string;
}

export async function getImageRating(
  imageId: string,
  isPrivate: boolean
): Promise<ImageRating | null> {
  const auth = getAuth();

  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (user) {
        try {
          const idToken = await user.getIdToken();

          const headers = {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          };

          const body = JSON.stringify({
            data: { imageId, isPrivate }
          });

          const response = await fetch(API2_URL + "getImageRating", {
            method: "POST",
            headers: headers,
            body: body,
          });

          if (!response.ok) {
            const errorData = await response.json();
            reject(new Error(errorData.error?.message || "Failed to get image rating"));
            return;
          }

          const responseData = await response.json();
          resolve(responseData.result as ImageRating | null);
        } catch (error) {
          reject(error);
        }
      } else {
        resolve(null);
      }
    });
  });
}

export async function addTag(prompt: string, categoryId: string, name: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AddTag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ prompt, category: categoryId, name }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to create tag");
  }
}

export async function getTagsForUser(): Promise<Tag[]> {
  const auth = getAuth();

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (!user) {
        resolve([]);
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        };
        const response = await fetch(`${API2_URL}getTagsForUser`, {
          method: "POST",
          headers: headers,
        });
        const res = await response.json();
        resolve(res.result as Tag[]);
      } catch {
        resolve([]);
      }
    });
  });
}

export async function updateTag(
  updates: {
    prompt?: string;
    category?: string;
    name?: string;
    description?: string;
    imageUrl?: string;
    onlyGenerators?: string[];
    disableForAnimated?: boolean;
  },
  tagId: string
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "UpdateTag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ ...updates, tagId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to update tag");
  }
}

export async function submitTag(tagId: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "SubmitTag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ tagId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to submit tag");
  }
}

let didFetchTags = false;
export async function getLiveTags(): Promise<void> {
  if (didFetchTags) {
    return;
  }
  didFetchTags = true;

  const response = await fetch(API2_URL + "getLiveTagsCached", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: null }),
  });
  const data = await response.json();
  useTagStore.getState().setLiveTags(data.result as Tag[]);
}

export async function saveTagToProfile(tagId: string) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const idToken = await user.getIdToken();
  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };

  const body = JSON.stringify({
    data: { tagId },
  });

  const response = await fetch(API2_URL + "SaveTagToProfile", {
    method: "POST",
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || "Failed to save tag");
  }
}

export async function getUserSavedTags(): Promise<Tag[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: null
    });

    const response: any = await fetch(
      API2_URL + "getUserSavedTags",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    return responseData.result as Tag[];
  }
  else {
    return [];
  }
}

export async function saveUsername(username: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");

  const idToken = await user.getIdToken();
  const response = await fetch(API2_URL + "SaveUsername", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ username }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || "Failed to save username");
  }
}

export async function getMyUsername(): Promise<string> {
  const auth = getAuth();

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (!user) {
        resolve("");
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const response = await fetch(
          API2_URL + "GetMyUsername",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          resolve("");
          return;
        }
        const data = await response.json();
        resolve(data.result as string);
      } catch {
        resolve("");
      }
    });
  });
}

export async function getPublicTagDataFromIds(
  tagIds: string[]
): Promise<Record<string, Tag>> {
  const response = await fetch(API2_URL + "GetPublicTagDataFromIds", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tagIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to get tag data");
  }

  return await response.json();
}

export async function unpublishTag(tagId: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "UnpublishTag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ tagId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to unpublish tag");
  }
}

export async function deleteAccount() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const idToken = await user.getIdToken();
  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(API2_URL + "DeleteAccount", {
    method: "POST",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error?.message || "Failed to delete account"
    );
  }
}

export async function deleteMultiplePrivateImages(imageIds: string[]) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { imageIds },
    });

    const response = await fetch(API2_URL + "deleteMultiplePrivateImages", {
      method: "POST",
      headers,
      body,
    });

    if (!response.ok) {
      throw new Error("Failed to delete private images");
    }

    return await response.json();
  } else {
    throw new Error("User not authenticated");
  }
}

export async function claimGift(giftId: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");

  const idToken = await user.getIdToken();
  const response = await fetch(API2_URL + "ClaimGift", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ giftId }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || "Failed to claim gift");
  }
}

export async function createFolder(name: string) {
  const newFolderNameObj = { name };

  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: newFolderNameObj
    });

    await fetch(
      API2_URL + "createFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

export interface FolderSql {
  id: string;
  owner_uid: string;
  name: string;
  create_date: Date;
  thumbnail_url?: string;
}

export async function getFolders(): Promise<any> {

  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: null
    });

    const response: any = await fetch(
      API2_URL + "getFolders",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    return responseData.result as FolderSql[];
  }
  else {
    return [];
  }
}

export async function addImageToFolder(
  imageId: string,
  folderId: string,
  isPrivate: boolean
) {
  const addImageToFolderData = { imageId, folderId, isPrivate };

  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: addImageToFolderData
    });

    await fetch(
      API2_URL + "addImageToFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

export async function getImagesInFolder(folderId: string, lastDate?: Date): Promise<any> {
  let input: any = { folderId };

  if (lastDate) {
    input = { ...input, cursor: lastDate };
  }

  // Return a Promise that resolves when user authentication state is available
  const auth = getAuth();

  return new Promise((resolve, reject) => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // User is authenticated, proceed with the API call
          const idToken = await user.getIdToken();

          const headers = {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          };

          const body = JSON.stringify({
            data: input
          });

          const response: any = await fetch(
            API2_URL + "getImagesInFolder",
            {
              method: "POST",
              headers: headers,
              body: body,
            }
          );

          if (!response.ok) {
            resolve([]);
            return;
          }

          const responseData = await response.json();
          resolve(responseData.result as any[]);
        } catch (error) {
          reject(error);
        }
      } else {
        resolve([]);
      }

      unsubscribe();
    });
  });
}

export async function getFoldersForImage(imageId: string) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { imageId }
    });

    const response: any = await fetch(
      API2_URL + "getFoldersForImage",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    return responseData.result as any[];
  }
  else {
    return [];
  }
}

export async function deleteImagesFromFolder(
  imageIds: string[],
  folderId: string
) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { imageIds, folderId }
    });

    await fetch(
      API2_URL + "deleteImagesFromFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

export async function deleteFolder(folderId: string) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { folderId }
    });

    await fetch(
      API2_URL + "deleteFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

export async function updateFolder(
  folderId: string,
  name: string,
  thumbnailUrl: string
) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { folderId, name, thumbnailUrl }
    });

    await fetch(
      API2_URL + "updateFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

export async function adminGetSubmittedTags(): Promise<Tag[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminGetSubmittedTags", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to get submitted tags");
  }

  return await response.json();
}

export async function adminUpdateTag(
  tagId: string,
  newStatus?: "live" | "rejected",
  rejectReason?: string,
  newPrompt?: string,
  isSilent?: boolean
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  let update: any = { tagId };
  if (newStatus) update.newStatus = newStatus;
  if (rejectReason) update.rejectReason = rejectReason;
  if (newPrompt) update.newPrompt = newPrompt;
  if (isSilent) update.isSilent = isSilent;

  const response = await fetch(API2_URL + "AdminUpdateTag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(update),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to update tag");
  }
}

export interface Flag {
  id: string;
  flag_create_date: any;
  flag_image_id: string;
  image_url: string;
  create_date: any;
  createDate?: any;
  edit_type: string;
}

export async function adminGetFlags(): Promise<Flag[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminGetFlags", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to get flags");
  }

  return await response.json();
}

export async function adminReviewFlag(
  action: "approve" | "delete_ancestors" | "delete_seed_ancestors",
  imageId: string
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminReviewFlag", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ action, imageId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to review flag");
  }
}

export async function adminGetTagInfo(tagId: string) {
  if (!tagId) {
    return null;
  }

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminGetTagInfo", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ tagId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to get tag info");
  }

  return await response.json() as Tag;
}

export async function adminLookupUser(query: string) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminLookupUser", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to lookup user");
  }

  return await response.json();
}

export async function adminGetTagArtists(): Promise<TagArtist[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminGetTagArtists", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to get tag artists");
  }

  return await response.json();
}

export async function adminUpdateTagArtist(
  tagArtistUid: string,
  newStatus: boolean
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminUpdateTagArtist", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ tagArtistUid, newStatus }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to update tag artist");
  }
}

export async function adminGiveFreePro(uid: string, timestamp: number) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");
  const idToken = await user.getIdToken();

  const response = await fetch(API2_URL + "AdminGiveFreePro", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ uid, timestamp }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || "Failed to grant free Pro");
  }
}

export async function upscaleToSDXL(
  imageId: string,
  isPrivate: boolean,
  promptData: Result,
  generator: string,
  isTest?: boolean
): Promise<{ id: string }> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const idToken = await user.getIdToken();
  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };

  const body = JSON.stringify({
    imageId,
    isPrivate,
    promptData,
    isTest: isTest || IS_TEST_CLOUD_FN,
    generator,
  });

  const response = await fetch(API2_URL + "UpscaleToSDXL", {
    method: "POST",
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error?.message || "Failed to upscale to SDXL"
    );
  }

  const data = await response.json();
  return data.result as { id: string };
}


export async function getTagStats(tagId: string) {
  // const result = await getTagStatsFn({
  //   tagId,
  // });

  // return result.data as {
  //   numImagesLastSevenDays: number;
  // };
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { tagId }
    });

    const response: any = await fetch(
      API2_URL + "getTagStats",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return {
        numImagesLastSevenDays: 0
      };
    }

    const responseData = await response.json();
    return responseData.result;
  }
  else {
    return {
      numImagesLastSevenDays: 0
    };
  }
}

export interface BatchImage {
  imageId: string;
  isPrivate: boolean;
}

export async function batchMoveImagesToFolder(
  images: BatchImage[],
  destinationFolderIds: string[],
  options?: {
    removeFromSaved?: boolean;
  }
) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { images, destinationFolderIds, ...options }
    });

    await fetch(
      API2_URL + "batchMoveImagesToFolder",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );
  }
}

interface ChatMessage {
  role: string;
  content: string;
}

export async function submitChat(messages: ChatMessage[]) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");

  const idToken = await user.getIdToken();
  const response = await fetch(API2_URL + "submitChat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ messages }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || "Chat failed");
  }
  const data = await response.json();
  return data.result as string;
}

export async function submitChatMetadata(
  messages: ChatMessage[],
  options?: {
    context?: string;
    resultId?: string;
    disableImage?: boolean;
    forceName?: string;
  }
) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: { messages, ...options }
    });

    const response: any = await fetch(
      "https://cluster6api.cdn2.io/Pornpen/submitChatMetadata",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();
    return responseData as {
      output: string;
      isImage?: boolean;
      tags?: string[];
    };
  }
  else {
    return [];
  }
}

export async function bindCCBillSubscription(
  subscriptionId: string,
  nextRenewalDate: string,
  userUid: string
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  const idToken = await user.getIdToken();
  const response = await fetch(API2_URL + "BindCCBillSubscription", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ subscriptionId, nextRenewalDate, userUid }),
  });

  if (!response.ok) throw new Error("Failed to bind CCBill subscription");
}

// The function for getting Sexy.AI user session id from Firebase JWT
export async function getSexyAIUserSession(): Promise<any> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const idToken = await user.getIdToken();

    const headers = {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      data: null
    });

    const response: any = await fetch(
      API2_URL + "getSexyAISessionID",
      {
        method: "POST",
        headers: headers,
        body: body,
      }
    );

    if (!response.ok) {
      return "";
    }

    const responseData = await response.json();

    if (responseData.hasError) {
      return "";
    } else {
      return responseData.payload;
    }
  }
  else {
    return [];
  }
}
